/**
 * Contracts list — drafts + sent + signed contracts visible to the actor.
 *
 * Three top-level tabs (Utkast / I signering / Signerat) split by signing
 * lifecycle (see ContractsTabbedList). The header carries a "+ Skapa
 * avtal" button that opens the template-picker modal so the create flow
 * starts from this page instead of /templates.
 *
 * Row layout (per ADR-pending naming refresh):
 *   Title          ← {property.name} lgh {apt_no}  (server-derived)
 *   Sub-line       ← {templateName} · {version} · {updatedAt}
 * Pills (Uppladdat / Behöver granskas / Ändringar begärda) sit beside the
 * title; status badge sits at the row's right edge.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { tenantApi, type ContractListRow } from '../api.js';
import { useTenantQuery } from '../query.js';
import { ContractsTabbedList } from '../components/ContractsTabbedList.js';
import { CreateContractModal } from '../components/CreateContractModal.js';
import { contractEditorPath } from '../components/DocumentEngine.js';
import { ExportCsvModal } from '../components/ExportCsvModal.js';
import { usePageTitle } from '../use-page-title.js';

const STATUS_LABEL: Record<ContractListRow['status'], string> = {
  draft: 'Utkast',
  pending: 'Klar för granskning',
  in_review: 'Granskas',
  awaiting_manual_signature: 'Väntar på underskrift',
  sent: 'Skickat',
  viewed: 'Öppnat',
  awaiting_signature: 'Väntar på signering',
  signed: 'Signerat',
  archived: 'Arkiverat',
  cancelled: 'Avbrutet',
};

const STATUS_CLASS: Record<ContractListRow['status'], string> = {
  draft: 'bg-stone-100 text-stone-700',
  pending: 'bg-blue-100 text-blue-800',
  in_review: 'bg-purple-100 text-purple-800',
  awaiting_manual_signature: 'bg-amber-100 text-amber-900',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-blue-100 text-blue-800',
  awaiting_signature: 'bg-amber-100 text-amber-900',
  signed: 'bg-green-100 text-green-800',
  archived: 'bg-stone-200 text-stone-600',
  cancelled: 'bg-red-100 text-red-800',
};

function statusLabel(c: ContractListRow): string {
  if (c.status === 'pending' && c.lastReviewStatus === 'approved') {
    return 'Granskning klar';
  }
  return STATUS_LABEL[c.status];
}

function statusClass(c: ContractListRow): string {
  if (c.status === 'pending' && c.lastReviewStatus === 'approved') {
    return 'bg-green-100 text-green-800';
  }
  return STATUS_CLASS[c.status];
}

export function ContractsListPage() {
  usePageTitle('Avtal');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce the live input by 200ms so each keystroke doesn't fire
  // a round-trip. The query is the cache-key input to useTenantQuery —
  // SWR refetches automatically when it changes.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchQuery) return;
    const timer = setTimeout(() => setSearchQuery(trimmed), 200);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  const { data, error, isLoading } = useTenantQuery(
    'contracts.list',
    (q) =>
      tenantApi.contracts.list(q ? { q: String(q) } : undefined),
    [searchQuery || undefined],
  );
  const contracts = data?.contracts ?? null;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const isSearching = searchQuery.length > 0;
  const hasContracts = (contracts?.length ?? 0) > 0;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-stone-500">Avtal</h2>
        <div className="flex items-center gap-2">
          {hasContracts && (
            <button
              type="button"
              onClick={() => setExportOpen(true)}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
            >
              Exportera till CSV
            </button>
          )}
          <Link
            to="/contracts/import"
            className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
          >
            Importera avtal
          </Link>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800"
          >
            + Skapa avtal
          </button>
        </div>
      </div>

      <div className="relative">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Sök på adress, fastighet, lägenhetsnr eller part…"
          className="w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm placeholder-stone-400 focus:border-stone-500 focus:outline-none"
        />
      </div>

      {/* Data-driven, Swedish-text region. `translate="no"` keeps browser
          auto-translation from rewriting these nodes out from under React —
          the source of the `removeChild` crash on group switch (REGTA-59).
          translate is inherited, so this also covers ContractsTabbedList. */}
      <div translate="no" className="space-y-3">
        {error && (
          <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {(error as Error).message}
          </div>
        )}
        {isLoading || contracts === null ? (
          <div className="text-sm text-stone-400">loading…</div>
        ) : contracts.length === 0 ? (
          <div className="rounded border border-dashed border-stone-300 p-6 text-center text-sm text-stone-500">
            {isSearching ? (
              <>Inga avtal matchar &ldquo;<span>{searchQuery}</span>&rdquo;.</>
            ) : (
              <>
                Inga avtal ännu. Klicka{' '}
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="underline"
                >
                  + Skapa avtal
                </button>{' '}
                för att börja från en mall.
              </>
            )}
          </div>
        ) : (
          <ContractsTabbedList
            contracts={contracts}
            renderRow={(c) => (
              <li key={c.id} className="hover:bg-stone-50">
                <Link to={contractEditorPath(c.id, c.documentEngine)} className="block p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="truncate font-medium">{c.title}</span>
                        {c.isImported && (
                          <span
                            className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sky-800"
                            title="Skapad från uppladdat originaldokument"
                          >
                            Uppladdat
                          </span>
                        )}
                        {c.needsReview && (
                          <span
                            className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-900"
                            title="Saknar data eller behöver granskas"
                          >
                            Behöver granskas
                          </span>
                        )}
                        {c.status === 'draft' && c.lastReviewStatus === 'rejected' && (
                          <span
                            className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-900"
                            title="Granskaren begärde ändringar"
                          >
                            Ändringar begärda
                          </span>
                        )}
                        {c.connection?.kind === 'child' && (
                          <span
                            className="truncate rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-700"
                            title={`Kopplad till ${c.connection.parent.title}`}
                          >
                            ↳ {c.connection.parent.title}
                          </span>
                        )}
                        {c.connection?.kind === 'parent' && (
                          <span
                            className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-700"
                            title={c.connection.children
                              .map((ch) => ch.title)
                              .join('\n')}
                          >
                            {c.connection.children.length} kopplade
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-stone-500">
                        <span>{c.templateName}</span> · <span>{c.templateVersion}</span> ·{' '}
                        <span>{new Date(c.updatedAt).toLocaleString('sv-SE')}</span>
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statusClass(
                        c,
                      )}`}
                    >
                      {statusLabel(c)}
                    </span>
                  </div>
                </Link>
              </li>
            )}
          />
        )}
      </div>

      {pickerOpen && <CreateContractModal onClose={() => setPickerOpen(false)} />}
      {exportOpen && contracts && (
        <ExportCsvModal
          contracts={contracts}
          query={searchQuery}
          onClose={() => setExportOpen(false)}
        />
      )}
    </section>
  );
}
